import type { AxiosInstance } from 'axios';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { getAnalysisErrorInfo, parseAnalysisError } from '../../utils/analysisErrors';
import { logger } from '../../utils/logger';
import { applySubStepProgress } from '../useSubStepProgress';
import type {
  AgentProgressEvent,
  AnalysisCompleteEvent,
  AnalysisErrorEvent,
  AnalysisProgressEvent,
  JobStatus,
  WorkflowStep,
} from './types';

interface HandlerDeps {
  jobId: string;
  api: AxiosInstance;
  setJobStatus: Dispatch<SetStateAction<JobStatus | null>>;
  touchProgress: () => void;
  lastSeqRef: MutableRefObject<number>;
  outOfOrderCountRef: MutableRefObject<number>;
}

export function createProgressHandler(deps: HandlerDeps) {
  const { jobId, api, setJobStatus, touchProgress, lastSeqRef, outOfOrderCountRef } = deps;

  return (data: AnalysisProgressEvent) => {
    if (data.jobId !== jobId) return;

    if (typeof data._seq === 'number') {
      if (data._seq <= lastSeqRef.current) {
        outOfOrderCountRef.current++;
        // If we see repeated out-of-order events, refetch from API to resync
        if (outOfOrderCountRef.current >= 3) {
          logger.warn('[useAnalysisJob] Multiple out-of-order events detected, refetching', {
            seq: data._seq,
            lastSeq: lastSeqRef.current,
            count: outOfOrderCountRef.current,
          });
          outOfOrderCountRef.current = 0;
          api
            .get(`/analysis/jobs/${jobId}`)
            .then((response) => {
              setJobStatus(response.data);
              lastSeqRef.current = -1;
            })
            .catch(() => {
              /* non-fatal */
            });
          return;
        }
      }
      lastSeqRef.current = Math.max(lastSeqRef.current, data._seq);
    }

    touchProgress();

    setJobStatus((prev) => {
      if (!prev) return null;
      if (prev.status === 'failed' || prev.status === 'completed' || prev.status === 'cancelled') {
        return prev;
      }

      // Sub-step progress update (e.g., clone progress, apk installs) — no
      // workflow object, just step + description. Shared with task execution.
      if (!data.workflow && data.step && data.description) {
        return applySubStepProgress(prev, data) ?? prev;
      }

      let workflowWithTimestamps = data.workflow;
      if (workflowWithTimestamps && workflowWithTimestamps.steps) {
        const enrichedSteps = workflowWithTimestamps.steps.map((step: WorkflowStep) => {
          const prevStep = prev.workflow?.steps?.find((s: WorkflowStep) => s.id === step.id);
          const startTime = step.startTime || prevStep?.startTime;
          const endTime = step.endTime || prevStep?.endTime;

          let durationMs = step.durationMs ?? null;
          let durationSeconds = step.durationSeconds ?? null;
          if (!durationMs && startTime && endTime) {
            durationMs = new Date(endTime).getTime() - new Date(startTime).getTime();
            durationSeconds = durationMs / 1000;
          }

          return { ...step, startTime, endTime, durationMs, durationSeconds };
        });

        // Preserve completed parent steps that aren't in the child's step list
        const newStepIds = new Set(enrichedSteps.map((s: WorkflowStep) => s.id));
        const preservedSteps = (prev.workflow?.steps ?? []).filter(
          (s: WorkflowStep) => !newStepIds.has(s.id) && s.status === 'completed',
        );

        const mergedSteps = [...preservedSteps, ...enrichedSteps].sort(
          (a: WorkflowStep, b: WorkflowStep) => (a.order ?? 0) - (b.order ?? 0),
        );
        const backendTotal = data.workflow?.totalSteps ?? enrichedSteps.length;

        workflowWithTimestamps = {
          ...workflowWithTimestamps,
          steps: mergedSteps,
          totalSteps: preservedSteps.length + backendTotal,
          completedSteps: mergedSteps.filter((s: WorkflowStep) => s.status === 'completed').length,
        };
      }

      return {
        ...prev,
        status: 'running' as const,
        workflow: workflowWithTimestamps,
      };
    });
  };
}

export function createCompleteHandler(deps: HandlerDeps) {
  const { jobId, api, setJobStatus, touchProgress, lastSeqRef } = deps;

  return async (data: AnalysisCompleteEvent) => {
    if (data.jobId !== jobId) return;

    if (typeof data._seq === 'number') {
      lastSeqRef.current = Math.max(lastSeqRef.current, data._seq);
    }

    if (data.result) {
      setJobStatus((prev) => {
        // Use server-provided completedAt timestamp
        const serverCompletedAt = data.completedAt;

        // Mark all running steps as completed - use server timestamps when available
        let finalWorkflow = prev?.workflow;
        if (finalWorkflow?.steps) {
          finalWorkflow = {
            ...finalWorkflow,
            steps: finalWorkflow.steps.map((step) => {
              if (step.status === 'running') {
                // Use server completedAt as endTime for running steps
                const endTime = step.endTime || serverCompletedAt;
                const durationMs =
                  step.startTime && endTime
                    ? new Date(endTime).getTime() - new Date(step.startTime).getTime()
                    : null;
                return {
                  ...step,
                  status: 'completed',
                  endTime,
                  durationMs,
                  durationSeconds: durationMs ? durationMs / 1000 : null,
                };
              }
              return step;
            }),
          };
        }

        // Mark all running agents as completed - use server timestamps
        let finalAgentProgress = prev?.agentProgress;
        if (finalAgentProgress?.agents) {
          finalAgentProgress = {
            ...finalAgentProgress,
            agents: finalAgentProgress.agents.map((agent) => {
              if (agent.status === 'running') {
                const completedAt = agent.completedAt || serverCompletedAt;
                const durationMs =
                  agent.startedAt && completedAt
                    ? new Date(completedAt).getTime() - new Date(agent.startedAt).getTime()
                    : null;
                return {
                  ...agent,
                  status: 'completed' as const,
                  completedAt,
                  durationMs,
                };
              }
              return agent;
            }),
            completed: finalAgentProgress.total,
            stepProgress: 1,
          };
        }

        return {
          ...prev,
          jobId: data.jobId,
          status: 'completed' as const,
          completedAt: serverCompletedAt,
          result: data.result,
          workflow: finalWorkflow,
          agentProgress: finalAgentProgress,
        };
      });
      touchProgress();
      return;
    }

    // Fallback: fetch from API
    const maxRetries = 3;
    const retryDelayMs = 2000;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await api.get(`/analysis/jobs/${jobId}`);
        const status: JobStatus = response.data;
        // Preserve agentProgress and workflow from previous state if API doesn't return them
        setJobStatus((prev) => ({
          ...status,
          agentProgress: status.agentProgress || prev?.agentProgress,
          workflow: status.workflow || prev?.workflow,
        }));
        touchProgress();
        return;
      } catch {
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
        } else {
          setJobStatus((prev) =>
            prev
              ? {
                  ...prev,
                  status: 'completed',
                  // Don't set completedAt here - we don't have server timestamp
                  error: 'Failed to load results - please refresh the page',
                }
              : null,
          );
        }
      }
    }
  };
}

export function createErrorHandler(deps: HandlerDeps) {
  const { jobId, setJobStatus, touchProgress, lastSeqRef } = deps;

  return (data: AnalysisErrorEvent) => {
    if (data.jobId !== jobId) return;

    if (typeof data._seq === 'number') {
      lastSeqRef.current = Math.max(lastSeqRef.current, data._seq);
    }

    // Parse and get user-friendly error info
    const parsedError = parseAnalysisError(data.error || data);
    const errorInfo = getAnalysisErrorInfo({
      ...parsedError,
      status: data.status,
    });

    touchProgress();

    setJobStatus((prev) => {
      if (!prev) return null;

      // Use server-provided timestamp if available
      const serverCompletedAt = data.completedAt;

      let updatedWorkflow = prev.workflow;
      const failedStep = data.step;
      if (failedStep && prev.workflow) {
        const steps = prev.workflow.steps.map((step) => {
          if (step.id === failedStep || step.title === failedStep) {
            return {
              ...step,
              status: 'failed',
              error: errorInfo.message,
              endTime: step.endTime || serverCompletedAt,
            };
          }
          return step;
        });
        updatedWorkflow = { ...prev.workflow, steps };
      }

      return {
        ...prev,
        status: 'failed',
        error: errorInfo.message,
        errorInfo,
        completedAt: serverCompletedAt,
        workflow: data.workflow || updatedWorkflow,
      };
    });
  };
}

export function createFailedHandler(deps: HandlerDeps) {
  const { jobId, setJobStatus, touchProgress, lastSeqRef } = deps;

  return (data: AnalysisErrorEvent) => {
    if (data.jobId !== jobId) return;

    if (typeof data._seq === 'number') {
      lastSeqRef.current = Math.max(lastSeqRef.current, data._seq);
    }

    // Parse and get user-friendly error info
    const parsedError = parseAnalysisError(data.error || data);
    const errorInfo = getAnalysisErrorInfo({
      ...parsedError,
      status: data.status,
    });

    touchProgress();

    setJobStatus((prev) => {
      if (!prev) return null;

      // Use server-provided timestamp if available
      const serverCompletedAt = data.completedAt;

      let updatedWorkflow = prev.workflow;
      const nestedStep =
        data.error && typeof data.error === 'object'
          ? ((data.error as Record<string, unknown>).step as string | undefined)
          : undefined;
      const stepIdentifier = nestedStep || data.step;
      if (prev.workflow && stepIdentifier) {
        const stepId = stepIdentifier;
        const steps = prev.workflow.steps.map((step) => {
          if (step.id === stepId || step.title === stepId) {
            return {
              ...step,
              status: 'failed',
              error: errorInfo.message,
              endTime: step.endTime || serverCompletedAt,
            };
          }
          return step;
        });
        updatedWorkflow = { ...prev.workflow, steps };
      }

      return {
        ...prev,
        status: 'failed',
        error: errorInfo.message,
        errorInfo,
        completedAt: serverCompletedAt,
        workflow: updatedWorkflow,
      };
    });
  };
}

export function createAgentProgressHandler(deps: HandlerDeps) {
  const { jobId, setJobStatus, touchProgress, lastSeqRef } = deps;

  return (data: AgentProgressEvent) => {
    if (data.jobId !== jobId) return;

    if (typeof data._seq === 'number') {
      lastSeqRef.current = Math.max(lastSeqRef.current, data._seq);
    }

    touchProgress();

    setJobStatus((prev) => {
      if (!prev) return null;
      if (prev.status === 'failed' || prev.status === 'completed' || prev.status === 'cancelled') {
        return prev;
      }

      return {
        ...prev,
        agentProgress: {
          agents: data.agents || [],
          completed: data.completed,
          total: data.total,
          stepProgress: data.stepProgress ?? (data.total > 0 ? data.completed / data.total : 0),
          avgDurationMs: data.avgDurationMs,
          serverTime: data.serverTime,
          // Capture client time when we receive server time - used for hybrid sync
          serverTimeReceivedAt: Date.now(),
        },
      };
    });
  };
}
